import React from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  RESTORATION_PHASES,
  RestorationPhase,
} from '../../types/teardown';
import {
  Layers,
  ListTodo,
  Check,
} from 'lucide-react';

export interface PhaseTrackerProps {
  activePhase?: RestorationPhase;
  onSelectPhase?: (phase: RestorationPhase) => void;
  className?: string;
  showMilestoneList?: boolean;
}

export interface PhaseStats {
  phase: RestorationPhase;
  sequence: number;
  title: string;
  shortCode: string;
  taskCount: number;
  completedTaskCount: number;
  subAssemblyCount: number;
  readyOrInstalledSubCount: number;
  installedSubCount: number;
  hardwareBagCount: number;
  photoCount: number;
  milestonePhotoCount: number;
  completionPercentage: number;
  isComplete: boolean;
}

export const PhaseTracker: React.FC<PhaseTrackerProps> = ({
  activePhase: propActivePhase,
  onSelectPhase,
  className = '',
  showMilestoneList = true,
}) => {
  const { state, dispatch } = useRestoMod();
  const currentActivePhase = propActivePhase || state.activePhase;

  const handleSelectPhase = (phase: RestorationPhase) => {
    if (onSelectPhase) {
      onSelectPhase(phase);
    } else {
      dispatch({ type: 'SET_ACTIVE_PHASE', payload: phase });
    }
  };

  const phaseKeys = Object.keys(RESTORATION_PHASES) as RestorationPhase[];

  // Calculate statistics for each phase
  const phaseStatsList: PhaseStats[] = phaseKeys.map((pKey) => {
    const meta = RESTORATION_PHASES[pKey];
    const phaseTasks = state.tasks.filter((t) => t.phase === pKey);
    const completedTasks = phaseTasks.filter((t) => t.status === 'completed');
    const phaseSubs = state.subAssemblies.filter((s) => s.phase === pKey);
    const readyOrInstalledSubs = phaseSubs.filter(
      (s) => s.status === 'ready_for_install' || s.status === 'installed'
    );
    const installedSubs = phaseSubs.filter((s) => s.status === 'installed');

    const subIds = new Set(phaseSubs.map((s) => s.id));
    const phaseBags = state.hardwareBags.filter((b) => subIds.has(b.subAssemblyId));
    const phasePhotos = state.photoLogs.filter((p) => p.phase === pKey);
    const milestonePhotos = phasePhotos.filter((p) => p.milestoneProof);

    // Compute completion percentage based on task and sub-assembly progress
    let completionPercentage = 0;
    const taskWeight = phaseTasks.length > 0 ? (completedTasks.length / phaseTasks.length) * 60 : 0;
    const subWeight = phaseSubs.length > 0 ? (readyOrInstalledSubs.length / phaseSubs.length) * 40 : 0;

    if (phaseTasks.length === 0 && phaseSubs.length === 0) {
      completionPercentage = 0;
    } else if (phaseTasks.length === 0) {
      completionPercentage = Math.round((readyOrInstalledSubs.length / Math.max(1, phaseSubs.length)) * 100);
    } else if (phaseSubs.length === 0) {
      completionPercentage = Math.round((completedTasks.length / Math.max(1, phaseTasks.length)) * 100);
    } else {
      completionPercentage = Math.min(100, Math.round(taskWeight + subWeight));
    }

    const isComplete =
      completionPercentage === 100 ||
      (phaseTasks.length > 0 &&
        completedTasks.length === phaseTasks.length &&
        phaseSubs.length > 0 &&
        installedSubs.length === phaseSubs.length);

    return {
      phase: pKey,
      sequence: meta.sequence,
      title: meta.title,
      shortCode: meta.shortCode,
      taskCount: phaseTasks.length,
      completedTaskCount: completedTasks.length,
      subAssemblyCount: phaseSubs.length,
      readyOrInstalledSubCount: readyOrInstalledSubs.length,
      installedSubCount: installedSubs.length,
      hardwareBagCount: phaseBags.length,
      photoCount: phasePhotos.length,
      milestonePhotoCount: milestonePhotos.length,
      completionPercentage,
      isComplete,
    };
  });

  // Overall project stats
  const totalTasks = state.tasks.length;
  const totalCompletedTasks = state.tasks.filter((t) => t.status === 'completed').length;
  const totalSubs = state.subAssemblies.length;
  const totalInstalledSubs = state.subAssemblies.filter((s) => s.status === 'installed').length;
  const overallPercentage =
    totalTasks > 0
      ? Math.round(
          phaseStatsList.reduce((acc, p) => acc + p.completionPercentage, 0) / phaseStatsList.length
        )
      : 0;

  const activeMetadata = RESTORATION_PHASES[currentActivePhase];
  const activeStats = phaseStatsList.find((p) => p.phase === currentActivePhase);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. Overall Project Restoration Header Bar */}
      <div className="bg-shop-darkest border border-shop-border rounded p-4 shadow-machined flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-safety-amber animate-pulse"></span>
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
              Vehicle Restoration Pipeline
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-shop-panel text-safety-amber border border-shop-border">
              7 PHASES
            </span>
          </div>
          <h2 className="text-base font-bold text-shop-highlight uppercase tracking-wide mt-1 flex items-center gap-2">
            <span>Phase {activeMetadata.sequence}: {activeMetadata.title}</span>
            <span className="text-xs font-mono text-safety-amber bg-shop-well px-2 py-0.5 rounded border border-shop-border">
              [{activeMetadata.shortCode}]
            </span>
          </h2>
          <p className="text-xs text-shop-muted mt-0.5 max-w-2xl line-clamp-1 md:line-clamp-none">
            {activeMetadata.description}
          </p>
        </div>

        {/* Global Progress Gauge */}
        <div className="flex items-center gap-4 shrink-0 bg-shop-dark p-3 rounded border border-shop-border">
          <div className="text-right">
            <div className="text-xs font-mono text-shop-muted uppercase">Overall Progress</div>
            <div className="text-xl font-bold font-mono text-safety-amber flex items-baseline justify-end gap-1">
              <span>{overallPercentage}%</span>
              <span className="text-xs text-shop-muted font-normal">
                ({totalCompletedTasks}/{totalTasks} tasks)
              </span>
            </div>
            <div className="text-[10px] text-shop-muted font-mono">
              {totalInstalledSubs}/{totalSubs} sub-assemblies installed
            </div>
          </div>
          <div className="w-12 h-12 rounded-full border-4 border-shop-panel flex items-center justify-center relative bg-shop-darkest">
            <svg className="w-12 h-12 -rotate-90 absolute inset-0" viewBox="0 0 36 36">
              <path
                className="text-shop-panel"
                strokeWidth="3.5"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-safety-amber transition-all duration-300"
                strokeDasharray={`${overallPercentage}, 100`}
                strokeWidth="3.5"
                strokeLinecap="round"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <span className="text-[11px] font-mono font-bold text-shop-highlight">
              {overallPercentage}%
            </span>
          </div>
        </div>
      </div>

      {/* 2. 7-Phase Interactive Navigation Strip (Min 44px touch targets) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {phaseStatsList.map((pStat) => {
          const isSelected = pStat.phase === currentActivePhase;

          return (
            <button
              key={pStat.phase}
              type="button"
              onClick={() => handleSelectPhase(pStat.phase)}
              className={`min-h-[56px] p-2.5 rounded text-left transition-all duration-100 flex flex-col justify-between border cursor-pointer select-none group relative overflow-hidden ${
                isSelected
                  ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b,0_4px_6px_rgba(0,0,0,0.5)]'
                  : 'bg-shop-dark text-shop-light border-shop-border hover:bg-shop-well hover:border-shop-steel active:translate-y-0.5'
              }`}
              aria-current={isSelected ? 'step' : undefined}
            >
              {/* Top row: Sequence number & completion badge */}
              <div className="flex items-center justify-between w-full">
                <span
                  className={`w-5 h-5 rounded flex items-center justify-center font-mono text-xs font-bold shrink-0 ${
                    isSelected
                      ? 'bg-safety-amber text-shop-darkest'
                      : pStat.isComplete
                      ? 'bg-safety-green text-shop-darkest'
                      : 'bg-shop-darkest text-shop-muted group-hover:text-shop-light border border-shop-border'
                  }`}
                >
                  {pStat.isComplete ? <Check className="w-3 h-3 stroke-[3]" /> : pStat.sequence}
                </span>

                <span
                  className={`text-[10px] font-mono font-bold px-1.5 py-0.2 rounded border ${
                    isSelected
                      ? 'bg-shop-darkest text-safety-amber border-safety-amber'
                      : 'bg-shop-darkest text-shop-muted border-shop-border'
                  }`}
                >
                  {pStat.shortCode}
                </span>
              </div>

              {/* Title */}
              <div className="my-1.5">
                <span className="text-xs font-bold uppercase tracking-tight line-clamp-1 block">
                  {pStat.title}
                </span>
                <span className="text-[10px] font-mono text-shop-muted block mt-0.5 truncate">
                  {pStat.subAssemblyCount} Bins • {pStat.taskCount} Tasks
                </span>
              </div>

              {/* Mini progress bar */}
              <div className="w-full bg-shop-darkest rounded-full h-1.5 overflow-hidden border border-shop-border">
                <div
                  className={`h-full transition-all duration-300 ${
                    pStat.isComplete
                      ? 'bg-safety-green'
                      : isSelected
                      ? 'bg-safety-amber'
                      : 'bg-shop-steel'
                  }`}
                  style={{ width: `${pStat.completionPercentage}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* 3. Active Phase Quick Statistics & Milestones */}
      {showMilestoneList && activeStats && (
        <div className="bg-shop-panel border border-shop-border rounded p-4 shadow-machined grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Phase metrics cards */}
          <div className="space-y-2">
            <h4 className="text-xs font-mono font-bold text-shop-muted uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-safety-amber" />
              Phase Metrics
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs font-mono">
              <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
                <span className="text-shop-muted text-[10px] block">STORAGE BINS</span>
                <span className="text-shop-highlight font-bold text-sm">
                  {activeStats.subAssemblyCount}
                </span>
                <span className="text-[10px] text-safety-green block">
                  {activeStats.installedSubCount} installed
                </span>
              </div>
              <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
                <span className="text-shop-muted text-[10px] block">HARDWARE BAGS</span>
                <span className="text-shop-highlight font-bold text-sm">
                  {activeStats.hardwareBagCount}
                </span>
                <span className="text-[10px] text-shop-muted block">Fasteners cataloged</span>
              </div>
              <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
                <span className="text-shop-muted text-[10px] block">TASKS DONE</span>
                <span className="text-safety-amber font-bold text-sm">
                  {activeStats.completedTaskCount} / {activeStats.taskCount}
                </span>
                <span className="text-[10px] text-shop-muted block">
                  {activeStats.taskCount > 0
                    ? Math.round((activeStats.completedTaskCount / activeStats.taskCount) * 100)
                    : 0}
                  % complete
                </span>
              </div>
              <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
                <span className="text-shop-muted text-[10px] block">PHOTO PROOFS</span>
                <span className="text-blueprint-cyan font-bold text-sm">
                  {activeStats.photoCount}
                </span>
                <span className="text-[10px] text-shop-muted block">
                  {activeStats.milestonePhotoCount} milestone proof
                </span>
              </div>
            </div>
          </div>

          {/* Suggested Milestones */}
          <div className="md:col-span-2 space-y-2">
            <h4 className="text-xs font-mono font-bold text-shop-muted uppercase tracking-wider flex items-center gap-1.5">
              <ListTodo className="w-3.5 h-3.5 text-safety-amber" />
              Suggested Restoration Milestones
            </h4>
            <div className="bg-shop-darkest border border-shop-border rounded p-3 space-y-1.5">
              {activeMetadata.suggestedMilestones.map((milestone, idx) => {
                // Check if any completed task title or photo includes this milestone or keyword
                const isMatched = state.tasks.some(
                  (t) =>
                    t.phase === currentActivePhase &&
                    t.status === 'completed' &&
                    t.title.toLowerCase().includes(milestone.toLowerCase().split(' ')[0])
                );

                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2.5 text-xs text-shop-light hover:text-shop-highlight py-1 border-b border-shop-border/40 last:border-b-0"
                  >
                    <span
                      className={`w-4 h-4 rounded flex items-center justify-center shrink-0 text-[10px] border ${
                        isMatched
                          ? 'bg-safety-green/20 text-safety-green border-safety-green'
                          : 'bg-shop-dark text-shop-muted border-shop-border'
                      }`}
                    >
                      {isMatched ? <Check className="w-3 h-3 stroke-[3]" /> : idx + 1}
                    </span>
                    <span className={`font-mono text-xs ${isMatched ? 'line-through text-shop-muted' : ''}`}>
                      {milestone}
                    </span>
                    {isMatched && (
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-safety-green/10 text-safety-green border border-safety-green/30 ml-auto uppercase font-bold">
                        VERIFIED
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
